import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import {
  LogOut,
  User,
  Settings,
  Menu,
  FileText,
  ClipboardList,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useIsMobileOrTablet } from "@/hooks/use-mobile";
import { useAuth } from "@/pages/auth/auth.context";
import { FeedbackButton } from "@/components/FeedbackButton";

const Header = () => {
  const { user, logout } = useAuth();
  // Below 1024, not below 640: an admin's six items need about 780px, and a
  // nav wider than the window drags every page's layout sideways with it.
  const isCompact = useIsMobileOrTablet();

  const isAdmin = user?.roles?.includes("ROLE_ADMIN");

  const handleLogout = () => {
    logout();
  };

  const NavLinks = () => (
    <>
      {user && (
        <>
          <Link
            to="/"
            className="text-foreground hover:text-primary font-medium"
          >
            All Upcoming Events
          </Link>
          <Link
            to="/documents"
            className="text-foreground hover:text-primary flex"
          >
            <FileText className="w-4 h-4 mr-2 mt-1" />
            My Documents
          </Link>
          <Link
            to="/employee-info"
            className="text-foreground hover:text-primary flex"
          >
            <User className="w-4 h-4 mr-2 mt-1" />
            Manage User Info
          </Link>
        </>
      )}
    </>
  );

  const AuthButtons = () => (
    <>
      {user ? (
        <>
          {/* Office work, not employee work: an employee has no client to
              bid for. The public /bid-request page is unchanged. */}
          {isAdmin && (
            <Link to="/bid-request">
              <Button variant="outline" className="flex items-center gap-2">
                <ClipboardList className="w-4 h-4" />
                Request a Bid
              </Button>
            </Link>
          )}
          {isAdmin && (
            <Link to="/admin/dashboard">
              <Button variant="outline" className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Admin Dashboard
              </Button>
            </Link>
          )}
          <Button
            variant="outline"
            className="flex items-center gap-2"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4" />
            Log Out
          </Button>
        </>
      ) : (
        <div className="space-x-4">
          <Link to="/signin">
            <Button variant="outline">Sign In</Button>
          </Link>
          <Link to="/signup">
            <Button>Sign Up</Button>
          </Link>
        </div>
      )}
    </>
  );

  return (
    <header className="border-b py-4">
      <div className="container mx-auto px-4">
        {isCompact ? (
          <div className="flex justify-between items-center">
            <Link to="/" className="text-foreground font-medium">
              Cash Casino Parties
            </Link>
            {/* Outside the sheet: it traps focus, and the feedback dialog
                renders outside it, so from in there the form could be read
                but not typed into. */}
            <div className="flex items-center gap-2">
              <FeedbackButton iconOnly />
              <Sheet>
                <SheetTrigger asChild>
                  {/* aria-label: an icon-only button otherwise announces as
                      just "button", and this is the only way into the nav. */}
                  <Button variant="ghost" size="icon" aria-label="Menu">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent>
                  <SheetHeader>
                    <SheetTitle>Menu</SheetTitle>
                  </SheetHeader>
                  <nav className="flex flex-col space-y-4 mt-6">
                    <NavLinks />
                  </nav>
                  <div className="flex flex-col space-y-4 mt-6">
                    <AuthButtons />
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <nav className="flex items-center space-x-6">
              <NavLinks />
            </nav>
            {/* Log Out ends up at the far edge, which is where the one
                button nobody wants to mis-click belongs. */}
            <div className="flex items-center space-x-4">
              <FeedbackButton />
              <AuthButtons />
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
